import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { getUserRole } from './AuthService';
import { isTokenExpired } from './tokenUtils';
import { isBillingRole, isClientRole, normalizeRole } from './roleUtils';
import { canContinuePreparedOffline } from '../offline/offlineSession';
import { rememberPostLoginRedirect } from '../utils/postLoginRedirect';
import OfflineVaultGate from '../components/OfflineVaultAccess';

/**
 * Route guard for /midwife/* pages.
 * Requires a valid, non-expired token AND a non-client role.
 * Client-role users are redirected to /client instead of /login
 * so they land on the correct dashboard rather than an error.
 */
const MidwifeRoute = ({ children }) => {
  const [role, setRole] = useState(() => normalizeRole(localStorage.getItem('userRole')));
  const [isRoleLoading, setIsRoleLoading] = useState(true);
  const token = localStorage.getItem('velaToken');
  const isInvalidToken = !token || isTokenExpired(token);
  const preparedOffline = canContinuePreparedOffline();

  useEffect(() => {
    let cancelled = false;

    const resolveRole = async () => {
      if (isInvalidToken || preparedOffline) { setIsRoleLoading(false); return; }

      setIsRoleLoading(true);
      try {
        const resolvedRole = await getUserRole(token);
        if (!cancelled) {
          setRole(normalizeRole(resolvedRole || localStorage.getItem('userRole')));
        }
      } catch (error) {
        console.error('Error checking midwife route role:', error);
        if (!cancelled) {
          setRole(normalizeRole(localStorage.getItem('userRole')));
        }
      } finally {
        if (!cancelled) {
          setIsRoleLoading(false);
        }
      }
    };

    resolveRole();

    return () => {
      cancelled = true;
    };
  }, [isInvalidToken, preparedOffline, token]);

  if (isInvalidToken && !preparedOffline) {
    rememberPostLoginRedirect();
    localStorage.removeItem('velaToken');
    localStorage.removeItem('userRole');
    return <Navigate to="/login" replace />;
  }

  if (isBillingRole(role)) return <Navigate to="/revenue-cycle" replace />;

  // Block client-role users from accessing midwife pages
  if (isClientRole(role)) {
    return <Navigate to="/client" replace />;
  }

  if (isRoleLoading) {
    return <LoadingSpinner />;
  }

  return <OfflineVaultGate>{children}</OfflineVaultGate>;
};

export default MidwifeRoute;
